import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  SimpleGrid,
  Image,
  Text,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  Divider,
  useDisclosure,
  useColorModeValue,
} from "@chakra-ui/react";
import { HamburgerIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { Link as RouterLink, useLocation } from "react-router-dom";

const HEADER_H = { base: "72px", md: "80px" };
const STRIP_H = { base: "46px", md: "52px" };

const NAV = [
  { label: "Home", to: "/" },
  {
    label: "People",
    items: [
      { label: "Lab Director", href: "https://bkantarci.nextconlab.ca/", external: true },
      { label: "Members", to: "/members" },
      { label: "Previous Members", to: "/alumni" },
    ],
  },
  {
    label: "Publications",
    items: [
      { label: "Publications", href: "https://bkantarci.nextconlab.ca/publications", external: true },
      { label: "Surveys & Tutorials", to: "/surveys-tutorials" },
    ],
  },
  { label: "Datasets", to: "/datasets" },
  {
    label: "News",
    items: [
      { label: "Announcements", to: "/news" },
      { label: "Media", to: "/media" },
    ],
  },
  { label: "Sponsors/Partners", to: "/SponsorsPartners" },
];

function isActivePath(pathname, item) {
  if (!item) return false;
  if (item.to === "/") return pathname === "/";
  if (item.to) return pathname.startsWith(item.to);
  if (item.items?.some((x) => x.to && pathname.startsWith(x.to))) return true;
  return false;
}

function DesktopLink({ label, to, active }) {
  const text = useColorModeValue("gray.800", "gray.100");
  const muted = useColorModeValue("gray.600", "gray.300");
  const hoverBg = useColorModeValue("blackAlpha.50", "whiteAlpha.100");
  const activeBg = useColorModeValue("green.50", "whiteAlpha.100");
  const activeRing = useColorModeValue("green.200", "green.400");

  return (
    <Button
      as={RouterLink}
      to={to}
      variant="ghost"
      size="sm"
      px={4}
      h="40px"
      borderRadius="999px"
      fontWeight="800"
      color={active ? text : muted}
      bg={active ? activeBg : "transparent"}
      _hover={{ bg: hoverBg }}
      position="relative"
    >
      {label}
      {active ? (
        <Box
          position="absolute"
          bottom="6px"
          left="14px"
          right="14px"
          height="2px"
          borderRadius="full"
          bg={activeRing}
        />
      ) : null}
    </Button>
  );
}

export default function Navbar() {
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activePath, setActivePath] = useState("/");

  useEffect(() => setActivePath(location.pathname), [location.pathname]);

  const activeMap = useMemo(() => {
    const map = {};
    NAV.forEach((item) => (map[item.label] = isActivePath(activePath, item)));
    return map;
  }, [activePath]);

  // “Not too white” surfaces
  const shellBg = useColorModeValue("gray.50", "gray.950");
  const headerBg = useColorModeValue("rgba(224, 231, 231, 0.92)", "rgba(10,12,16,0.75)");
  const border = useColorModeValue("blackAlpha.200", "whiteAlpha.200");
  const shadow = useColorModeValue("0 10px 30px rgba(0,0,0,0.08)", "0 10px 30px rgba(0,0,0,0.45)");
  const brand = useColorModeValue("gray.900", "white");
  const sub = useColorModeValue("gray.600", "gray.300");
  const hoverBg = useColorModeValue("blackAlpha.50", "whiteAlpha.100");
  const menuBg = useColorModeValue("rgba(252,252,253,0.98)", "rgba(20,22,26,0.98)");
  const stripBg = useColorModeValue("rgba(245,247,250,0.95)", "rgba(255,255,255,0.06)");

  const contentMaxW = "1240px";

  return (
    <>
      {/* Fixed header + strip */}
      <Box position="fixed" top="0" left="0" right="0" zIndex={1000}>
        {/* Header */}
        <Box
          bg={headerBg}
          backdropFilter="saturate(180%) blur(10px)"
          borderBottom="1px solid"
          borderColor={border}
          boxShadow={shadow}
        >
          <Flex h={HEADER_H} align="center" justify="center">
  <Flex
    w="full"
    maxW={contentMaxW}
    px={{ base: 4, md: 6 }}
    align="center"
    justify="flex-start"
    gap={{ base: 3, md: 6 }}
  >
    {/* Brand (fixed left column width for better nav positioning) */}
    {/* Brand / Logo Section */}
<Box
  display="flex"
  alignItems="center"
  mr={{ base: 4, md: 8 }}   // space between logo and nav
>
  <HStack spacing={3} align="center">
    
    {/* Logo Icon Container */}
    <Box
      borderRadius="12px"
      p="6px"
      bg={useColorModeValue("whiteAlpha.700", "whiteAlpha.50")}
      border="1px solid"
      borderColor={border}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Image
        src="nextcon_logo.png"
        alt="NEXTCON Lab Logo"
        h="48px"
        w="auto"
        objectFit="contain"
      />
    </Box>

    {/* Text Branding */}
    <Box lineHeight="1.1">
      <Text
        fontWeight="900"
        fontSize={{ base: "md", md: "lg" }}
        color="green.500"
        letterSpacing="0.3px"
      >
        NEXTCON Lab
      </Text>

      <Text
        fontSize={{ base: "xs", md: "sm" }}
        color={sub}
        fontWeight="500"
      >
        University of Ottawa • SCVIC
      </Text>
    </Box>

  </HStack>
</Box>

    {/* Desktop nav (better position: center-right, not extreme right) */}
    <HStack
      spacing={1}
      display={{ base: "none", lg: "flex" }}
      flex="1"
      justify="flex-end"
      pr={2}
    >
      {NAV.map((item) => {
        if (item.items?.length) {
          const active = activeMap[item.label];
          const btnText = useColorModeValue("gray.700", "gray.200");
          const btnActive = useColorModeValue("green.800", "green.200");

          return (
            <Menu key={item.label} isLazy>
              <MenuButton
                as={Button}
                variant="ghost"
                size="sm"
                h="34px"
                px={3}
                borderRadius="99px"
                fontWeight="800"
                rightIcon={<ChevronDownIcon />}
                color={active ? btnActive : btnText}
                bg={active ? useColorModeValue("green.50", "whiteAlpha.100") : "transparent"}
                _hover={{ bg: hoverBg }}
              >
                {item.label}
              </MenuButton>

              <MenuList
                bg={menuBg}
                borderColor={border}
                py={2}
                borderRadius="16px"
                boxShadow={useColorModeValue("lg", "dark-lg")}
                overflow="hidden"
              >
                {item.items.map((subItem) =>
                  subItem.external ? (
                    <MenuItem
                      key={subItem.label}
                      as="a"
                      href={subItem.href}
                      target="_blank"
                      rel="noreferrer"
                      py={2}
                    >
                      {subItem.label}
                    </MenuItem>
                  ) : (
                    <MenuItem key={subItem.label} as={RouterLink} to={subItem.to} py={2}>
                      {subItem.label}
                    </MenuItem>
                  )
                )}
              </MenuList>
            </Menu>
          );
        }

        return (
          <DesktopLink
            key={item.label}
            label={item.label}
            to={item.to}
            active={activeMap[item.label]}
          />
        );
      })}
    </HStack>

    {/* Mobile hamburger */}
    <IconButton
      display={{ base: "inline-flex", lg: "none" }}
      aria-label="Open menu"
      icon={<HamburgerIcon />}
      onClick={onOpen}
      variant="ghost"
      borderRadius="14px"
      h="44px"
      w="44px"
      _hover={{ bg: hoverBg }}
      ml="auto"
    />
  </Flex>
</Flex>

          {/* Title strip */}
          <Box bg={stripBg} borderTop="1px solid" borderColor={border}>
            <Flex h={STRIP_H} align="center" justify="center">
              <Box w="full" maxW={contentMaxW} px={{ base: 4, md: 6 }}>
                <Box
                    overflowX={{ base: "auto", md: "visible" }}
                    whiteSpace={{ base: "nowrap", md: "normal" }}
                    textAlign={{ base: "left", md: "center" }}
                    mx="auto"
                    sx={{
                    WebkitOverflowScrolling: "touch",
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                    }}
                >
                    <Text
                    display={{ base: "inline-block", md: "block" }}
                    fontSize={{ base: "sm", md: "md" }}
                    fontFamily="'Rockwell', serif"
                    fontWeight="600"              // slightly bold, not heavy
                    letterSpacing="0.3px"
                    lineHeight="1.15"
                    py={0}
                    >
                    <Box as="span" color="green.800">
                        Smart Connected Vehicles Innovation Centre (SCVIC)
                    </Box>

                    <Box as="span" color="green.500">
                        {" "} &amp;{" "}
                    </Box>

                    <Box as="span" color="green.500">
                        Next
                    </Box>

                    <Box as="span" color="green.800">
                        {" "}Generation{" "}
                    </Box>

                    <Box as="span" color="green.500">
                        Co
                    </Box>

                    <Box as="span" color="green.800">
                        mmunications and Computing{" "}
                    </Box>

                    <Box as="span" color="green.500">
                        N
                    </Box>

                    <Box as="span" color="green.800">
                        etworks (
                    </Box>

                    <Box as="span" color="green.500" fontWeight="700">
                        NEXTCON
                    </Box>

                    <Box as="span" color="green.800">
                        ) Lab
                    </Box>
                    </Text>
                </Box>
                    {/* small hint for mobile only */}
                    <Text
                        display={{ base: "block", md: "none" }}
                        mt={1}
                        fontSize="xs"
                        color="gray.500"
                        textAlign="center"
                    >
                        Swipe →
                    </Text>
                </Box>
            </Flex>
          </Box>
        </Box>
      </Box>

      {/* Mobile drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent bg={shellBg}>
          <DrawerCloseButton />
          <DrawerHeader>
            <Text fontWeight="900">Menu</Text>
          </DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={1}>
              {NAV.map((item) => {
                if (item.items?.length) {
                  return (
                    <Box key={item.label}>
                      <Text fontSize="sm" fontWeight="800" color={sub} mt={2} mb={1}>
                        {item.label}
                      </Text>
                      <VStack align="stretch" spacing={1} pl={2}>
                        {item.items.map((subItem) =>
                          subItem.external ? (
                            <Button
                              key={subItem.label}
                              as="a"
                              href={subItem.href}
                              target="_blank"
                              rel="noreferrer"
                              variant="ghost"
                              justifyContent="flex-start"
                              borderRadius="14px"
                              onClick={onClose}
                              _hover={{ bg: hoverBg }}
                            >
                              {subItem.label}
                            </Button>
                          ) : (
                            <Button
                              key={subItem.label}
                              as={RouterLink}
                              to={subItem.to}
                              variant="ghost"
                              justifyContent="flex-start"
                              borderRadius="14px"
                              onClick={onClose}
                              _hover={{ bg: hoverBg }}
                            >
                              {subItem.label}
                            </Button>
                          )
                        )}
                      </VStack>
                      <Divider my={3} borderColor={border} />
                    </Box>
                  );
                }

                return (
                  <Button
                    key={item.label}
                    as={RouterLink}
                    to={item.to}
                    variant="ghost"
                    justifyContent="flex-start"
                    borderRadius="14px"
                    fontWeight="800"
                    onClick={onClose}
                    _hover={{ bg: hoverBg }}
                  >
                    {item.label}
                  </Button>
                );
              })}
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Hero content (kept here) */}
      <Box
        bg={shellBg}
        pt={{
          base: `calc(${HEADER_H.base} + ${STRIP_H.base} + 12px)`,
          md: `calc(${HEADER_H.md} + ${STRIP_H.md} + 16px)`,
          lg: `calc(${HEADER_H.md} + ${STRIP_H.md} + 18px)`,
        }}
      >
        <Box w="full" maxW={contentMaxW} mx="auto" px={{ base: 4, md: 6 }} pt={{ base: 1, md: 1 }}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 6, md: 8 }} alignItems="center">
            <Flex justify="center">
              <Image
                src="NEXTCON-image001.jpg"
                alt="NEXTCON image 001"
                w="full"
                maxW="640px"
                h="auto"
                borderRadius="2xl"
                boxShadow="md"
                objectFit="contain"
              />
            </Flex>

            <Flex justify="center">
              <Image
                src="NEXTCON-KANATA.jpg"
                alt="NEXTCON Kanata"
                w="full"
                maxW="760px"
                h="auto"
                borderRadius="2xl"
                boxShadow="md"
                objectFit="contain"
              />
            </Flex>
          </SimpleGrid>

          <Flex justify="center" mt={{ base: 4, md: 5 }}>
            <Text
              fontSize={{ base: "sm", md: "md" }}
              fontFamily="'Times New Roman', Times, serif"
              textAlign="center"
              color={useColorModeValue("gray.800", "gray.100")}
              maxW="1100px"
              px={2}
            >
              <b>Director: Dr. Burak Kantarci,</b> Professor and University Research Chair in AI-Enabled Secure
              Networking for Smart Critical Infrastructures, School of Electrical Engineering and Computer Science,
              University of Ottawa
            </Text>
          </Flex>

          <Divider mt={{ base: 4, md: 6 }} borderColor={border} />
        </Box>
      </Box>
    </>
  );
}